import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  MdClose,
  MdAutoAwesome,
  MdKeyboardArrowDown,
  MdCheck,
  MdUndo,
  MdSearch,
  MdRefresh,
} from "react-icons/md";
import "./ModelGenerationDrawer.css";
import {
  useModelGenerationDrawer,
  SHARED_OPTIONS,
  FIELD_OPTIONS,
  MAX_NAME_CHARACTERS,
  MAX_PROMPT_CHARACTERS,
} from "../../customHook/ModelGenerationDrawer/useModelGenerationDrawer";

export { SHARED_OPTIONS, FIELD_OPTIONS, MAX_NAME_CHARACTERS, MAX_PROMPT_CHARACTERS };

const CustomSelectField = ({
  label,
  displayLabel,
  placeholder,
  compact = false,
  value,
  options = [],
  isCustom = false,
  onChange,
  onToggleCustom,
  fieldKey,
  openDropdownKey,
  setOpenDropdownKey,
  error,
}) => {
  const isOpen = openDropdownKey === fieldKey;
  const [searchQuery, setSearchQuery] = useState("");
  const [openUpward, setOpenUpward] = useState(false);
  const searchInputRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setOpenUpward(false);
    } else {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const drawerBody = triggerRef.current.closest(".model-gen-body");

        let spaceBelow = viewportHeight - rect.bottom;
        let spaceAbove = rect.top;

        if (drawerBody) {
          const bodyRect = drawerBody.getBoundingClientRect();
          const bodySpaceBelow = bodyRect.bottom - rect.bottom;
          const bodySpaceAbove = rect.top - bodyRect.top;
          spaceBelow = Math.min(spaceBelow, bodySpaceBelow);
          spaceAbove = Math.min(spaceAbove, bodySpaceAbove);
        }

        // If space below is less than 215px and space above is greater, open upward!
        if (spaceBelow < 215 && spaceAbove > 140) {
          setOpenUpward(true);
        } else {
          setOpenUpward(false);
        }

        // Ensure trigger itself is kept comfortably visible
        try {
          triggerRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
        } catch (_) {}
      }

      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const trimmedQuery = searchQuery.trim().toLowerCase();
  const filteredOptions = options.filter((opt) =>
    opt.toLowerCase().includes(trimmedQuery)
  );

  return (
    <div
      data-dropdown-key={fieldKey}
      className={`model-gen-select-container flex flex-col gap-0.5 text-left w-full relative ${
        isOpen ? "z-40" : "z-0"
      }`}
    >
      <div className="flex items-center justify-between">
        <label
          className={`${
            compact ? "text-[9.5px]" : "text-[10.5px]"
          } font-semibold text-slate-700 tracking-wide`}
        >
          {displayLabel || label} <span className="text-red-500 font-bold">*</span>
        </label>
        {isCustom && (
          <button
            type="button"
            onClick={() => onToggleCustom()}
            className="text-[9.5px] font-semibold text-primary hover:text-indigo-700 flex items-center gap-0.5 cursor-pointer bg-transparent border-none p-0"
            title="Switch back to presets"
          >
            <MdUndo size={10} /> Preset Options
          </button>
        )}
      </div>

      {!isCustom ? (
        <div className={`relative w-full ${isOpen ? "z-50" : ""}`}>
          <div
            ref={triggerRef}
            onClick={(e) => {
              e.stopPropagation();
              setOpenDropdownKey(isOpen ? null : fieldKey);
            }}
            className={`model-gen-select-trigger ${compact ? "is-compact" : ""} ${
              isOpen ? "is-open" : ""
            } ${error ? "is-error" : ""}`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpenDropdownKey(isOpen ? null : fieldKey);
              }
            }}
          >
            <span className={`truncate ${!value ? "text-slate-400 font-normal" : ""}`}>
              {value || placeholder || `Select ${displayLabel || label}`}
            </span>
            <MdKeyboardArrowDown
              size={15}
              className={`text-slate-400 shrink-0 transition-transform duration-200 ${
                isOpen ? "rotate-180 text-slate-800" : ""
              }`}
            />
          </div>
          {error && (
            <span className="text-red-500 text-[10px] font-semibold mt-0.5 block">
              {error}
            </span>
          )}

          {isOpen && (
            <div className={`model-gen-dropdown-menu ${openUpward ? "open-upward" : ""}`}>
              {/* Search Bar at Top */}
              <div className="model-gen-dropdown-search-wrapper">
                <div className="relative flex items-center">
                  <MdSearch
                    size={14}
                    className="absolute left-2 text-slate-400 pointer-events-none"
                  />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={compact ? "Search" : `Search ${label.toLowerCase()}`}
                    className="model-gen-dropdown-search-input"
                    onClick={(e) => e.stopPropagation()}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchQuery("");
                        searchInputRef.current?.focus();
                      }}
                      className="absolute right-1.5 text-slate-400 hover:text-slate-600 border-none bg-transparent p-0 cursor-pointer flex items-center justify-center"
                      title="Clear search"
                    >
                      <MdClose size={12} />
                    </button>
                  )}
                </div>
              </div>

              {/* FIRST OPTION: Custom option */}
              <div className="model-gen-dropdown-custom-item">
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleCustom(searchQuery.trim() || undefined);
                    setOpenDropdownKey(null);
                  }}
                  className="flex items-center px-2.5 py-1.5 text-[11px] font-semibold text-slate-800 hover:text-slate-950 hover:bg-slate-100 cursor-pointer transition-colors select-none gap-1.5"
                >
                  <span className="text-slate-900 font-extrabold text-sm shrink-0 leading-none">+</span>
                  <span className="truncate">
                    {searchQuery.trim()
                      ? `Custom: "${searchQuery.trim()}"`
                      : "Custom"}
                  </span>
                </div>
              </div>

              {/* Scrollable Presets List */}
              <div className="model-gen-dropdown-options">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((opt) => {
                    const isActive = value === opt;
                    return (
                      <div
                        key={opt}
                        onClick={(e) => {
                          e.stopPropagation();
                          onChange(opt);
                          setOpenDropdownKey(null);
                        }}
                        className={`flex items-center justify-between px-2.5 py-1.5 text-[11px] rounded cursor-pointer transition-colors select-none ${
                          isActive
                            ? "bg-slate-100 font-bold text-slate-900"
                            : "text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                        }`}
                      >
                        <span className="truncate">{opt}</span>
                        {isActive && (
                          <MdCheck size={13} className="text-slate-900 shrink-0 ml-1" />
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="px-3 py-2 text-[10.5px] text-slate-400 italic text-center">
                    No matching presets
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="relative w-full">
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={
              fieldKey === "age"
                ? "Enter age (18 - 80)"
                : `Type custom ${label.toLowerCase()}`
            }
            className={`model-gen-control ${error ? "is-error" : ""}`}
            autoFocus
          />
          {error && (
            <span className="text-red-500 text-[10px] font-semibold mt-0.5 block">
              {error}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const ModelGenerationDrawer = ({
  isOpen,
  onClose,
  onGenerate,
  defaultGender = "",
  activeJobsCount = 0,
}) => {
  const {
    gender,
    handleGenderChange,
    name,
    handleNameChange,
    age,
    handleAgeChange,
    ethnicity,
    handleEthnicityChange,
    skinTone,
    handleSkinToneChange,
    bodyType,
    handleBodyTypeChange,
    hairType,
    handleHairTypeChange,
    hairLength,
    handleHairLengthChange,
    hairColor,
    handleHairColorChange,
    clothing,
    handleClothingChange,
    footwear,
    handleFootwearChange,
    background,
    handleBackgroundChange,
    customPrompt,
    handleCustomPromptChange,
    errors,
    customModes,
    toggleCustomMode,
    isSubmitting,
    openDropdownKey,
    setOpenDropdownKey,
    drawerBodyRef,
    isFormValid,
    genderOptions,
    isSection1DropdownOpen,
    isSection2DropdownOpen,
    isSection3DropdownOpen,
    handleReset,
    handleSubmit,
  } = useModelGenerationDrawer({
    isOpen,
    onClose,
    onGenerate,
    defaultGender,
  });

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div className="model-gen-drawer-overlay" onClick={onClose}>
      <aside
        className="model-gen-drawer-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Generate Fashion Model"
      >
        {/* Header */}
        <div className="model-gen-header px-4 py-2 sm:px-4 sm:py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-800 border border-slate-200/80 flex items-center justify-center shrink-0">
              <MdAutoAwesome size={18} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-sm sm:text-base font-bold text-slate-900 m-0 tracking-tight">
                  Generate AI Model
                </h2>
                {activeJobsCount > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-800 border border-slate-200 shrink-0">
                    {activeJobsCount} in queue
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-500 m-0 font-medium">
                Configure physical features, styling, and studio scene attributes
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-800 flex items-center justify-center cursor-pointer transition-all"
            aria-label="Close drawer"
          >
            <MdClose size={18} />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div
          ref={drawerBodyRef}
          className="model-gen-body flex-1 overflow-y-auto py-4 px-4 sm:px-5 pb-20 space-y-3.5 text-left"
        >
          {/* SECTION 1: Core Identity */}
          <div
            className={`model-gen-card space-y-2.5 ${
              isSection1DropdownOpen ? "has-open-dropdown z-30" : "z-1"
            }`}
          >
            <div className="flex items-center gap-2 pb-1.5 border-b border-slate-100">
              <span className="model-gen-step-badge">1</span>
              <span className="text-[11px] font-bold text-slate-800 tracking-wide uppercase">
                Identity & Demographics
              </span>
            </div>

            {/* Model Name Input */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="model-gen-name"
                  className="text-[10.5px] font-semibold text-slate-700 tracking-wide"
                >
                  Model Name <span className="text-red-500 font-bold">*</span>
                </label>
                <span className="text-[10px] font-semibold text-slate-400">
                  {name.length}/{MAX_NAME_CHARACTERS}
                </span>
              </div>
              <input
                id="model-gen-name"
                type="text"
                value={name}
                maxLength={MAX_NAME_CHARACTERS}
                onChange={handleNameChange}
                placeholder="e.g. Elena V., Julian M., Aria Vance..."
                className={`model-gen-control ${errors.name ? "is-error" : ""}`}
              />
              {errors.name && (
                <span className="text-red-500 text-[10px] font-semibold block">
                  {errors.name}
                </span>
              )}
            </div>

            {/* Gender & Age Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-0.5 items-start">
              {/* Gender Dropdown */}
              <div className="flex flex-col gap-0.5 text-left w-full" data-dropdown-key="gender">
                <div className="flex items-center justify-between">
                  <label htmlFor="model-gen-gender" className="text-[10.5px] font-semibold text-slate-700 tracking-wide">
                    Gender <span className="text-red-500 font-bold">*</span>
                  </label>
                </div>
                <div className="relative h-[32px]">
                  <select
                    id="model-gen-gender"
                    value={gender}
                    onChange={(e) => handleGenderChange(e.target.value)}
                    className={`model-gen-control appearance-none pr-8 cursor-pointer text-[11px] h-[32px] ${
                      errors.gender ? "is-error" : ""
                    } ${!gender ? "text-slate-400 font-normal" : ""}`}
                  >
                    <option value="" className="text-slate-400">
                      Select Gender
                    </option>
                    <option value="female" className="text-slate-800 font-medium">Women</option>
                    <option value="male" className="text-slate-800 font-medium">Men</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-400">
                    <MdKeyboardArrowDown size={15} />
                  </div>
                </div>
                {errors.gender && (
                  <span className="text-red-500 text-[10px] font-semibold mt-0.5 block">
                    {errors.gender}
                  </span>
                )}
              </div>

              {/* Age Group Dropdown */}
              <CustomSelectField
                label="Age"
                value={age}
                options={SHARED_OPTIONS.age}
                isCustom={customModes.age}
                onChange={handleAgeChange}
                onToggleCustom={(initialVal) => toggleCustomMode("age", initialVal)}
                fieldKey="age"
                openDropdownKey={openDropdownKey}
                setOpenDropdownKey={setOpenDropdownKey}
                error={errors.age}
              />
            </div>
          </div>

          {/* SECTION 2: Physical Features, Skin & Hair */}
          <div
            className={`model-gen-card space-y-2.5 ${
              isSection2DropdownOpen ? "has-open-dropdown z-30" : "z-1"
            }`}
          >
            <div className="flex items-center gap-2 pb-1.5 border-b border-slate-100">
              <span className="model-gen-step-badge">2</span>
              <span className="text-[11px] font-bold text-slate-800 tracking-wide uppercase">
                Physical Features & Appearance
              </span>
            </div>

            {/* Demographics & Body Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <CustomSelectField
                label="Ethnicity"
                value={ethnicity}
                options={SHARED_OPTIONS.ethnicity}
                isCustom={customModes.ethnicity}
                onChange={handleEthnicityChange}
                onToggleCustom={(initialVal) => toggleCustomMode("ethnicity", initialVal)}
                fieldKey="ethnicity"
                openDropdownKey={openDropdownKey}
                setOpenDropdownKey={setOpenDropdownKey}
                error={errors.ethnicity}
              />

              <CustomSelectField
                label="Skin Tone"
                value={skinTone}
                options={SHARED_OPTIONS.skinTone}
                isCustom={customModes.skinTone}
                onChange={handleSkinToneChange}
                onToggleCustom={(initialVal) => toggleCustomMode("skinTone", initialVal)}
                fieldKey="skinTone"
                openDropdownKey={openDropdownKey}
                setOpenDropdownKey={setOpenDropdownKey}
                error={errors.skinTone}
              />

              <div className="sm:col-span-2">
                <CustomSelectField
                  label="Body Type"
                  value={bodyType}
                  options={genderOptions.bodyType}
                  isCustom={customModes.bodyType}
                  onChange={handleBodyTypeChange}
                  onToggleCustom={(initialVal) => toggleCustomMode("bodyType", initialVal)}
                  fieldKey="bodyType"
                  openDropdownKey={openDropdownKey}
                  setOpenDropdownKey={setOpenDropdownKey}
                  error={errors.bodyType}
                />
              </div>
            </div>

            {/* Hair Specifications (Type, Length, Color) */}
            <div className="pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between pb-1.5 mb-2 border-b border-slate-100/80">
                <span className="text-[10px] font-bold text-slate-700 uppercase tracking-wider">
                  Hair Specifications
                </span>
                <span className="text-[9.5px] font-medium text-slate-400">
                  Type · Length · Color
                </span>
              </div>
              <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                <CustomSelectField
                  label="Hair Type"
                  displayLabel="Type"
                  placeholder="Select Type"
                  compact
                  value={hairType}
                  options={genderOptions.hairType}
                  isCustom={customModes.hairType}
                  onChange={handleHairTypeChange}
                  onToggleCustom={(initialVal) => toggleCustomMode("hairType", initialVal)}
                  fieldKey="hairType"
                  openDropdownKey={openDropdownKey}
                  setOpenDropdownKey={setOpenDropdownKey}
                  error={errors.hairType}
                />

                <CustomSelectField
                  label="Hair Length"
                  displayLabel="Length"
                  placeholder="Select Length"
                  compact
                  value={hairLength}
                  options={genderOptions.hairLength}
                  isCustom={customModes.hairLength}
                  onChange={handleHairLengthChange}
                  onToggleCustom={(initialVal) => toggleCustomMode("hairLength", initialVal)}
                  fieldKey="hairLength"
                  openDropdownKey={openDropdownKey}
                  setOpenDropdownKey={setOpenDropdownKey}
                  error={errors.hairLength}
                />

                <CustomSelectField
                  label="Hair Color"
                  displayLabel="Color"
                  placeholder="Select Color"
                  compact
                  value={hairColor}
                  options={SHARED_OPTIONS.hairColor}
                  isCustom={customModes.hairColor}
                  onChange={handleHairColorChange}
                  onToggleCustom={(initialVal) => toggleCustomMode("hairColor", initialVal)}
                  fieldKey="hairColor"
                  openDropdownKey={openDropdownKey}
                  setOpenDropdownKey={setOpenDropdownKey}
                  error={errors.hairColor}
                />
              </div>
            </div>
          </div>

          {/* SECTION 3: Wardrobe, Footwear & Scene Setting */}
          <div
            className={`model-gen-card space-y-2.5 ${
              isSection3DropdownOpen ? "has-open-dropdown z-30" : "z-1"
            }`}
          >
            <div className="flex items-center gap-2 pb-1.5 border-b border-slate-100">
              <span className="model-gen-step-badge">3</span>
              <span className="text-[11px] font-bold text-slate-800 tracking-wide uppercase">
                Styling, Footwear & Studio Setting
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <div className="sm:col-span-2">
                <CustomSelectField
                  label="Clothing"
                  value={clothing}
                  options={genderOptions.clothing}
                  isCustom={customModes.clothing}
                  onChange={handleClothingChange}
                  onToggleCustom={(initialVal) => toggleCustomMode("clothing", initialVal)}
                  fieldKey="clothing"
                  openDropdownKey={openDropdownKey}
                  setOpenDropdownKey={setOpenDropdownKey}
                  error={errors.clothing}
                />
              </div>

              <CustomSelectField
                label="Footwear"
                value={footwear}
                options={genderOptions.footwear}
                isCustom={customModes.footwear}
                onChange={handleFootwearChange}
                onToggleCustom={(initialVal) => toggleCustomMode("footwear", initialVal)}
                fieldKey="footwear"
                openDropdownKey={openDropdownKey}
                setOpenDropdownKey={setOpenDropdownKey}
                error={errors.footwear}
              />

              <CustomSelectField
                label="Studio Background"
                value={background}
                options={SHARED_OPTIONS.background}
                isCustom={customModes.background}
                onChange={handleBackgroundChange}
                onToggleCustom={(initialVal) => toggleCustomMode("background", initialVal)}
                fieldKey="background"
                openDropdownKey={openDropdownKey}
                setOpenDropdownKey={setOpenDropdownKey}
                error={errors.background}
              />
            </div>
          </div>

          {/* SECTION 4: Custom Fine-tuning Prompt */}
          <div className="model-gen-card space-y-2.5">
            <div className="flex items-center justify-between pb-1.5 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <span className="model-gen-step-badge">4</span>
                <label
                  htmlFor="model-gen-prompt"
                  className="text-[11px] font-bold text-slate-800 tracking-wide uppercase cursor-pointer m-0"
                >
                  Custom Prompt / Creative Notes
                </label>
              </div>
              <span className="text-[10px] font-semibold text-slate-400">
                {customPrompt.length}/{MAX_PROMPT_CHARACTERS}
              </span>
            </div>
            <textarea
              id="model-gen-prompt"
              rows={3}
              maxLength={MAX_PROMPT_CHARACTERS}
              value={customPrompt}
              onChange={handleCustomPromptChange}
              placeholder="Optional: Specify nuances, eye color, pose, facial expression, jewelry, or lighting details..."
              className="model-gen-textarea"
            />
          </div>
        </div>

        {/* Footer Actions */}
        <div className="model-gen-footer px-4 py-2.5 sm:px-4 sm:py-2.5 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-8 px-3.5 flex items-center justify-center text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer border border-slate-200 bg-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={isSubmitting}
              className="h-8 px-3.5 flex items-center justify-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer border border-slate-200 bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <MdRefresh size={14} className="text-slate-400" />
              <span>Reset</span>
            </button>
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isFormValid}
            className="h-8 px-4 flex items-center justify-center gap-2 text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 active:scale-[0.98] rounded-lg shadow-sm hover:shadow transition-all cursor-pointer border border-transparent disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <MdAutoAwesome size={14} className={isSubmitting ? "animate-spin text-amber-300" : "text-amber-300"} />
            <span>{isSubmitting ? "Submitting..." : "Generate Model"}</span>
          </button>
        </div>
      </aside>
    </div>,
    document.body
  );
};

export default ModelGenerationDrawer;
